import { ElementType, ReactNode } from "react";
import { Tile } from "@carbon/react";
import "./statCard.scss";

export interface StatCardProps {
  /** Already translated by the caller, same convention as `PageHeader`'s `title`. */
  label: string;
  /**
   * Pre-formatted by the caller — this component does no numeric or currency
   * formatting of its own. Every current KPI grid (`SellerKpiMetricCards`,
   * the analytics KPI row)
   * already formats the number before building its card list (see
   * `KPIs/utils/kpiHelpers.ts`'s `formatCurrency`/`formatPercentage`, and the
   * bare `.toLocaleString()` counts), so this stays a plain string/number
   * rather than duplicating that logic here.
   */
  value: string | number;
  /**
   * Optional supporting copy under the value — the pre-Carbon `KpiCard`'s
   * `description` (e.g. "Purchase requests awaiting review"). Omitted when
   * a card has none, same convention as `EmptyState`'s `description`.
   */
  caption?: string;
  /**
   * Optional leading icon, matching the pre-Carbon `KpiCard`'s `card.icon` —
   * every one of the 4 KPI grids sets one (all `@carbon/icons-react` glyphs
   * today). Typed as `React.ElementType` to match `KPIs/utils/kpiHelpers.ts`'s
   * own `KpiCard.icon` field rather than inventing a narrower shape.
   */
  icon?: ElementType;
  /**
   * Optional rich content under the caption (e.g. a trend indicator with an
   * arrow icon) for the rare card whose supporting copy is not plain text.
   */
  footer?: ReactNode;
}

/**
 * A single KPI stat: a label, a big value, and optional supporting caption
 * and leading icon. Built on Carbon `Tile` (not `ClickableTile` — no current
 * KPI card is clickable; `KpiCard`, the 4 metric-card grids, and the
 * dashboard summary cards in `KPIs.tsx` are all static, so there is nothing
 * to force the clickable variant here).
 *
 * Deliberately does NOT carry the pre-Carbon `KpiCard`'s `color` (a Tailwind
 * background class like `bg-blue-500`) or a `currency` field — those aren't
 * shared-primitive concerns: `color` is a per-card accent that the flow
 * migrating these screens onto Carbon tokens gets to redecide, and
 * `currency` is only ever used by `kpiHelpers` to pick that same color, never
 * rendered. No current card shows a delta/trend or a loading state of its
 * own (the KPI screens swap a `SkeletonLoad` in for the *whole* grid while
 * loading, at the container level — see `KPIs.tsx` and `SellerAnalytics.tsx` —
 * not per card), so this primitive doesn't invent those props either.
 *
 * Owned by the primitives layer — the Dashboards flow's migrated KPI grids
 * consume this in place of `KpiCard`.
 */
const StatCard = ({
  label,
  value,
  caption,
  icon: Icon,
  footer,
}: StatCardProps) => (
  <Tile className="stat-card">
    <div className="stat-card__header">
      {Icon ? <Icon className="stat-card__icon" aria-hidden="true" /> : null}
      <p className="stat-card__label">{label}</p>
    </div>
    <p className="stat-card__value">{value}</p>
    {caption ? <p className="stat-card__caption">{caption}</p> : null}
    {footer}
  </Tile>
);

export default StatCard;
